/**
 * Stage 6 — Real Tallivo inventory client + shadow persistence wiring.
 *
 * `createTallivoInventoryClient()` calls the server-side
 * `commerce-inventory-read` edge function. The browser NEVER talks to
 * Tallivo directly. All authentication, tenant validation, HMAC signing,
 * and field-stripping happen server-side.
 *
 * `persistShadowDiffs(...)` fire-and-forgets normalized diagnostic rows to
 * `commerce-inventory-shadow-diff`. Never blocks the storefront.
 */
import { supabase } from "@/integrations/supabase/client";
import type {
  TallivoInventoryClient,
  TallivoInventoryQuery,
  TallivoInventoryRecord,
  InventoryDiscrepancy,
  CommerceInventoryFreshness,
} from "./inventory";

const READ_TIMEOUT_MS = 1500;

/** Client that shadow-mode uses. Wired via setTallivoInventoryClient(...). */
export function createTallivoInventoryClient(): TallivoInventoryClient {
  return {
    async fetch(query: TallivoInventoryQuery): Promise<TallivoInventoryRecord[]> {
      const ctrl = new AbortController();
      const timeout = setTimeout(() => ctrl.abort(), READ_TIMEOUT_MS);
      try {
        const { data, error } = await supabase.functions.invoke(
          "commerce-inventory-read",
          {
            body: {
              business_id: query.businessId,
              product_ids: query.productIds ?? [],
              variant_ids: query.variantIds ?? [],
              location_ids: query.fulfillmentLocationIds ?? [],
            },
          },
        );
        if (error) throw error;
        const records = Array.isArray((data as { records?: unknown[] } | null)?.records)
          ? ((data as { records: TallivoInventoryRecord[] }).records)
          : [];
        return records;
      } catch (err) {
        // Never let this reach the storefront render path.
        console.warn("[tallivoInventoryClient] fetch failed", err);
        return [];
      } finally {
        clearTimeout(timeout);
      }
    },
  };
}

let inflight: Promise<unknown> | null = null;

/**
 * Fire-and-forget persistence. Coalesces overlapping calls so a bursty page
 * never hammers the edge function.
 */
export function persistShadowDiffs(
  diffs: InventoryDiscrepancy[],
  ctx: { businessId: string; storefrontId?: string | null; locationScope?: string | null;
        supabaseFreshness?: CommerceInventoryFreshness | null;
        tallivoFreshness?: CommerceInventoryFreshness | null; } = { businessId: "" },
) {
  if (!diffs.length || !ctx.businessId) return;
  const payload = diffs.map((d) => ({
    business_id: ctx.businessId,
    storefront_id: ctx.storefrontId ?? null,
    product_id: d.productId,
    variant_id: d.variantId,
    supabase_status: d.supabaseStatus,
    tallivo_status: d.tallivoStatus,
    supabase_quantity: d.supabaseQuantity,
    tallivo_quantity: d.tallivoQuantity,
    supabase_policy: d.supabasePolicy,
    tallivo_policy: d.tallivoPolicy,
    supabase_freshness: ctx.supabaseFreshness ?? null,
    tallivo_freshness: ctx.tallivoFreshness ?? null,
    location_scope: ctx.locationScope ?? null,
    mismatch_type: d.mismatchType,
  }));

  const send = async () => {
    try {
      // Only admins may record discrepancies; skip for shoppers.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      await supabase.functions.invoke("commerce-inventory-shadow-diff", {
        body: { diffs: payload },
      });
    } catch (err) {
      console.warn("[persistShadowDiffs] send failed", err);
    }
  };
  // Coalesce: if a send is already in flight, chain after it.
  inflight = inflight ? inflight.then(send).catch(send) : send();
}
